import { useHistory, useLocation, useParams } from "react-router-dom";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import PageHeader from "../../layouts/PageHeader";
import { Form, Row, Col, Alert, Spinner, Modal } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import {
    Save,
    X,
    AlertTriangle,
    CheckCircle,
    Plus,
    Trash2,
    Paperclip,
    File,
    FilePlus,
    Lock,
    LockOpen,
    WandSparkles,
    LayoutTemplate,
    MessageSquare,
    FilePenLine,
    Copy,
} from 'lucide-react';
import "react-datepicker/dist/react-datepicker.css";
import axios from '../../../services/AxiosInstance';
import { getApiUrl } from "../../../utils/ApiUtils";
import swal from "sweetalert";
import AccountSelect from '../Common/AccountSelect';
import AccountPickerScope from '../Common/AccountPickerScope';
import {
    accountOption,
    buildAccountPickerOptions,
    countDefaultPickerAccounts,
    hasCompanyAccounts,
} from '../../../utils/accountPickerOptions';
import ManualTemplatePickerModal from './ManualTemplatePickerModal';
import { recordTemplateUsage } from '../../../services/BookkeepingTemplateService';
import {
    amountToCents,
    findFiscalYearForDate,
    formatLocalDate,
    getManualEntryIssues,
    getPostingTotals,
    resolveTemplatePostingSide,
    rowsFromCopiedVerification,
    rowsFromBookkeepingTemplate,
    rowsFromTemplateEvaluation,
} from './manualBookkeepingUtils';
import './manual-bookkeeping-entry.css';

import { toast } from 'sonner'

// Helper function to format numbers as Swedish currency (SEK)
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount);
};

const createDraftSnapshot = ({ date, description, reference, comments, attachments, rows }) => JSON.stringify({
    date: formatLocalDate(date),
    description,
    reference,
    comments,
    attachments: attachments.map((attachment) => attachment.attachmentId || attachment.id),
    rows: rows.map((row) => ({
        account_id: row.account_id,
        debit: row.debit,
        credit: row.credit,
    })),
});

const parseVerificationDate = (value) => {
    const localDate = String(value || '').slice(0, 10);
    return localDate ? new Date(`${localDate}T12:00:00`) : new Date();
};

const normalizeExistingAttachment = (attachment) => ({
    ...attachment,
    id: attachment.attachment_id || attachment.attachmentId,
    attachmentId: attachment.attachment_id || attachment.attachmentId,
    original_filename: attachment.file_name || attachment.fileName || attachment.original_filename,
    file_size: attachment.file_size ?? attachment.fileSize ?? 0,
    file_type: attachment.file_type || attachment.fileType,
    blobName: attachment.blob_name || attachment.blobName,
    fileUrl: attachment.file_url || attachment.fileUrl,
    isOriginalAttachment: true,
});

const CreateVerification = ({ mode = 'create' }) => {
    const { companyId, verificationId } = useParams();
    const history = useHistory();
    const location = useLocation();
    const isEditMode = mode === 'edit' && Boolean(verificationId);
    const copySource = location.state?.copyFrom || null;
    const returnTo = location.state?.returnTo || location.state?.from || `/${companyId}/verifications`;

    const getInitialDate = () => {
        const selectedFiscalYear = location.state?.fiscalYear;
        if (!selectedFiscalYear) return new Date();

        const today = new Date();
        const todayString = formatLocalDate(today);
        const start = String(selectedFiscalYear.start_date || '').slice(0, 10);
        const end = String(selectedFiscalYear.end_date || '').slice(0, 10);
        if (start <= todayString && todayString <= end) return today;
        return end ? new Date(`${end}T12:00:00`) : today;
    };

    // Initialize state variables for manual verification creation
    const [date, setDate] = useState(getInitialDate);
    const [description, setDescription] = useState(copySource?.description || "");
    const [reference, setReference] = useState("");
    const [comments, setComments] = useState("");
    const [attachments, setAttachments] = useState([]);
    const [originalAttachments, setOriginalAttachments] = useState([]);
    const [editSource, setEditSource] = useState(null);
    const [loadingVerification, setLoadingVerification] = useState(isEditMode);
    const [uploadingFiles, setUploadingFiles] = useState(false);
    const [fiscalYears, setFiscalYears] = useState([]);
    const [validationAttempted, setValidationAttempted] = useState(false);
    const [showTemplatePicker, setShowTemplatePicker] = useState(false);
    const [appliedTemplate, setAppliedTemplate] = useState(null);
    const [showAttachmentsSection, setShowAttachmentsSection] = useState(false);
    const [showCommentsSection, setShowCommentsSection] = useState(false);

    const [previewAttachment, setPreviewAttachment] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewText, setPreviewText] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const descriptionRef = useRef(null);

    const [manualRows, setManualRows] = useState(() => copySource
        ? rowsFromCopiedVerification(copySource)
        : [
            { id: Date.now(), account_id: "", account_number: "", account_name: "", debit: "", credit: "" },
            { id: Date.now() + 1, account_id: "", account_number: "", account_name: "", debit: "", credit: "" }
        ]);
    const [accounts, setAccounts] = useState([]);
    // "Visa alla konton" — the picker's default view is the basuppsättning,
    // accounts with bokföring and own accounts (OC-5); the full chart on request.
    const [showAllAccounts, setShowAllAccounts] = useState(false);
    const [loading, setLoading] = useState(false);
    const [savingVerification, setSavingVerification] = useState(false);
    const [error, setError] = useState(null);
    const initialDraftRef = useRef(createDraftSnapshot({
        date,
        description,
        reference,
        comments,
        attachments,
        rows: manualRows,
    }));

    const fetchAccounts = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const accountsApiUrl = getApiUrl(`${companyId}/accounting/accounts?scope=company`);
            const response = await axios.get(accountsApiUrl, { withCredentials: true });

            if (response.data.accounts && Array.isArray(response.data.accounts)) {
                const formattedAccounts = response.data.accounts.map(account => ({
                    ...accountOption(account),
                    balance: account.balance || 0,
                    isActive: account.is_active !== false,
                }));

                formattedAccounts.sort((a, b) => a.accountNumber.localeCompare(b.accountNumber));
                setAccounts(formattedAccounts);
            } else {
                throw new Error("Kunde inte hämta konton");
            }
        } catch (error) {
            console.error('Error fetching accounts:', error);
            setError('Det gick inte att hämta kontolistan. Försök igen senare.');
        } finally {
            setLoading(false);
        }
    }, [companyId]);

    const fetchFiscalYears = useCallback(async () => {
        try {
            const response = await axios.get(
                getApiUrl(`${companyId}/accounting/fiscalyears`),
                { withCredentials: true }
            );
            setFiscalYears(response.data.fiscalYears || []);
        } catch (fetchError) {
            console.error('Error fetching fiscal years:', fetchError);
            setFiscalYears([]);
        }
    }, [companyId]);

    const fetchVerificationForEdit = useCallback(async () => {
        if (!isEditMode) return;

        setLoadingVerification(true);
        setError(null);

        try {
            const [verificationResponse, attachmentsResult] = await Promise.all([
                axios.get(
                    getApiUrl(`${companyId}/verification/${verificationId}`),
                    { withCredentials: true }
                ),
                axios.get(
                    getApiUrl(`${companyId}/accounting/verifications/${verificationId}/attachments`),
                    { withCredentials: true }
                ).catch(() => null),
            ]);

            const source = verificationResponse.data;
            if (!source || !Array.isArray(source.transactions)) {
                throw new Error('Kunde inte hämta verifikatet.');
            }
            if (source.is_locked) {
                throw new Error('Det här verifikatet är låst och kan inte redigeras.');
            }

            const nextDate = parseVerificationDate(source.date);
            const nextDescription = source.description || '';
            const nextReference = source.reference || '';
            const nextComments = source.notes || '';
            const nextRows = rowsFromCopiedVerification(source);
            const nextOriginalAttachments = (attachmentsResult?.data?.attachments || [])
                .map(normalizeExistingAttachment);

            setEditSource(source);
            setDate(nextDate);
            setDescription(nextDescription);
            setReference(nextReference);
            setComments(nextComments);
            setManualRows(nextRows);
            setOriginalAttachments(nextOriginalAttachments);
            setShowAttachmentsSection(nextOriginalAttachments.length > 0);
            setShowCommentsSection(Boolean(nextComments));
            initialDraftRef.current = createDraftSnapshot({
                date: nextDate,
                description: nextDescription,
                reference: nextReference,
                comments: nextComments,
                attachments: [],
                rows: nextRows,
            });
        } catch (fetchError) {
            console.error('Error fetching verification for editing:', fetchError);
            setError(fetchError.response?.data?.message || fetchError.message || 'Det gick inte att hämta verifikatet.');
        } finally {
            setLoadingVerification(false);
        }
    }, [companyId, isEditMode, verificationId]);

    // The rows' current selections stay selectable whatever the view (a
    // template or an edited verifikat may point outside the default view).
    const pickerOptions = useMemo(
        () => buildAccountPickerOptions(accounts, {
            showAll: showAllAccounts,
            pinned: manualRows.map((row) => row.account_id),
        }),
        [accounts, showAllAccounts, manualRows]
    );

    // Initialize the component - fetch accounts when component mounts
    useEffect(() => {
        fetchAccounts();
        fetchFiscalYears();
    }, [fetchAccounts, fetchFiscalYears]);

    useEffect(() => {
        fetchVerificationForEdit();
    }, [fetchVerificationForEdit]);

    // File handling functions
    const handleFileUpload = async (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;

        setUploadingFiles(true);
        const newAttachments = [];

        try {
            for (const file of files) {
                // Validate file size (10MB limit)
                if (file.size > 10 * 1024 * 1024) {
                    toast.error(`Filen "${file.name}" är för stor. Maximal filstorlek är 10MB.`);
                    continue;
                }

                // Validate file type
                const allowedTypes = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx', '.txt'];
                const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
                if (!allowedTypes.includes(fileExtension)) {
                    toast.error(`Filtypen för "${file.name}" stöds inte.`);
                    continue;
                }

                // Create form data for upload
                const formData = new FormData();
                formData.append('file', file);

                try {

                    const uploadResponse = await axios.post(
                        getApiUrl(`${companyId}/accounting/verifications/attachments`),
                        formData,
                        {
                            withCredentials: true,
                            headers: {
                                'Content-Type': 'multipart/form-data'
                            }
                        }
                    );

                    if (uploadResponse.data.success) {
                        newAttachments.push({
                            id: uploadResponse.data.attachment.attachmentId, // Use server-generated attachment ID
                            attachmentId: uploadResponse.data.attachment.attachmentId,
                            blobName: uploadResponse.data.attachment.blobName,
                            fileUrl: uploadResponse.data.attachment.fileUrl,
                            filename: uploadResponse.data.attachment.fileName,
                            original_filename: file.name,
                            file_size: uploadResponse.data.attachment.fileSize,
                            file_type: uploadResponse.data.attachment.fileType,
                            upload_date: new Date().toISOString()
                        });
                    } else {
                        throw new Error(uploadResponse.data.message || "Upload failed");
                    }
                } catch (uploadError) {
                    toast.error(`Det gick inte att ladda upp "${file.name}". Försök igen.`);
                }
            }

            if (newAttachments.length > 0) {
                setAttachments(prev => [...prev, ...newAttachments]);
                toast.success(`${newAttachments.length} fil(er) har laddats upp.`);
            }
        } catch (error) {
            setError('Det gick inte att ladda upp filerna. Försök igen senare.');
        } finally {
            setUploadingFiles(false);
            // Reset file input
            event.target.value = '';
        }
    };

    const removeAttachment = (attachmentId) => {
        swal({
            title: "Ta bort underlag?",
            text: "Filen tas bort permanent.",
            icon: "warning",
            buttons: ["Avbryt", "Ta bort"],
            dangerMode: true,
        }).then(async (willDelete) => {
            if (willDelete) {
                try {
                    await axios.delete(
                        getApiUrl(`${companyId}/accounting/verifications/attachments/${attachmentId}`),
                        { withCredentials: true }
                    );
                    setAttachments(prev => prev.filter(att => att.attachmentId !== attachmentId));
                    if (previewAttachment?.attachmentId === attachmentId) closeAttachmentPreview();
                    toast.success('Underlaget har tagits bort.');
                } catch (deleteError) {
                    toast.error('Det gick inte att ta bort underlaget. Försök igen.');
                }
            }
        });
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const canPreviewFile = (fileName) => {
        if (!fileName) return false;
        const extension = fileName.toLowerCase().split('.').pop();
        return ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'txt', 'csv', 'json', 'xml'].includes(extension);
    };

    const closeAttachmentPreview = () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewAttachment(null);
        setPreviewUrl(null);
        setPreviewText(null);
        setPreviewLoading(false);
    };

    const openAttachmentPreview = async (attachment) => {
        if (!canPreviewFile(attachment.original_filename)) return;

        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewAttachment(attachment);
        setPreviewUrl(null);
        setPreviewText(null);
        setPreviewLoading(true);

        const fileName = attachment.original_filename || '';
        const isText = /\.(txt|csv|json|xml)$/i.test(fileName);

        try {
            const response = await axios.get(
                getApiUrl(`${companyId}/accounting/verifications/attachments/${attachment.attachmentId}/fullsize`),
                {
                    withCredentials: true,
                    responseType: isText ? 'text' : 'blob',
                    timeout: 10000,
                }
            );

            if (isText) setPreviewText(response.data);
            else setPreviewUrl(URL.createObjectURL(response.data));
        } catch (previewError) {
            toast.error('Det gick inte att förhandsgranska underlaget.');
            setPreviewAttachment(null);
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleAddRow = () => {
        setManualRows([...manualRows, {
            id: `${Date.now()}-${manualRows.length}`,
            account_id: "",
            account_number: "",
            account_name: "",
            debit: "",
            credit: ""
        }]);
    };

    const handleRemoveRow = (index) => {
        if (manualRows.length > 2) { // Keep minimum 2 rows
            setManualRows(manualRows.filter((row, rowIndex) => rowIndex !== index));
        } else {
            toast.info("Du måste ha minst två transaktioner för ett verifikat.");
        }
    };

    const handleRowChange = (index, field, value) => {
        const newRows = [...manualRows];
        if (field === "account_id") {
            const selectedAccount = accounts.find((option) => option.value === value);
            const currentRow = newRows[index];
            const accountNumber = selectedAccount?.accountNumber || '';
            const templateSide = currentRow.template_side_rule
                ? resolveTemplatePostingSide(currentRow.template_type, accountNumber, currentRow.template_side_rule)
                : null;
            const enteredAmount = currentRow.debit || currentRow.credit;

            newRows[index] = {
                ...currentRow,
                account_id: selectedAccount?.value || '',
                account_number: accountNumber,
                account_name: selectedAccount?.accountName || '',
                template_side: templateSide,
                suggested_side: templateSide || (currentRow.template_side_rule === 'auto' ? 'auto' : currentRow.suggested_side),
                debit: templateSide === 'debit' && enteredAmount ? enteredAmount : (templateSide === 'credit' ? '' : currentRow.debit),
                credit: templateSide === 'credit' && enteredAmount ? enteredAmount : (templateSide === 'debit' ? '' : currentRow.credit),
            };
        } else if (field === "debit" && newRows[index].template_side === 'credit') {
            return;
        } else if (field === "credit" && newRows[index].template_side === 'debit') {
            return;
        } else if (field === "debit") {
            // If debit is entered, clear credit
            newRows[index] = {
                ...newRows[index],
                debit: value,
                credit: value ? "" : newRows[index].credit
            };
        } else if (field === "credit") {
            // If credit is entered, clear debit
            newRows[index] = {
                ...newRows[index],
                credit: value,
                debit: value ? "" : newRows[index].debit
            };
        } else {
            newRows[index][field] = value;
        }
        setManualRows(newRows);
    };

    const { debitCents, creditCents } = useMemo(
        () => getPostingTotals(manualRows),
        [manualRows]
    );
    const totalDebit = debitCents / 100;
    const totalCredit = creditCents / 100;
    const differenceCents = Math.abs(debitCents - creditCents);
    const hasPostingAmounts = debitCents > 0 || creditCents > 0;
    const isBalanced = hasPostingAmounts && debitCents === creditCents;
    const activeFiscalYear = useMemo(
        () => findFiscalYearForDate(fiscalYears, date),
        [fiscalYears, date]
    );
    const isFiscalYearLocked = !activeFiscalYear || activeFiscalYear.state === 'closed';
    const validationIssues = useMemo(
        () => getManualEntryIssues({ description, rows: manualRows, fiscalYear: activeFiscalYear }),
        [description, manualRows, activeFiscalYear]
    );
    const issueKeys = useMemo(
        () => new Set(validationIssues.map((issue) => issue.key)),
        [validationIssues]
    );

    const handleFillDifference = () => {
        if (differenceCents === 0) return;
        const targetSide = debitCents > creditCents ? 'credit' : 'debit';
        const targetIndex = [...manualRows]
            .map((row, index) => ({ row, index }))
            .reverse()
            .find(({ row }) => (
                amountToCents(row.debit) === 0
                && amountToCents(row.credit) === 0
                && (!row.template_side || row.template_side === targetSide)
            ))?.index;

        if (targetIndex === undefined) {
            toast.info('Lägg till en tom konteringsrad först.');
            return;
        }

        const amount = (differenceCents / 100).toFixed(2).replace('.', ',');
        handleRowChange(targetIndex, targetSide, amount);
    };

    const handleApplyTemplate = async (template, evaluation = null) => {
        const { rows, unresolvedCount } = evaluation
            ? rowsFromTemplateEvaluation(evaluation, template, accounts)
            : rowsFromBookkeepingTemplate(template, accounts);
        if (rows.length === 0) {
            throw new Error('Mallen innehåller inga konteringsrader.');
        }

        const hasEnteredRows = manualRows.some((row) => row.account_id || row.debit || row.credit);
        if (hasEnteredRows) {
            const shouldReplace = await swal({
                title: 'Ersätta nuvarande rader?',
                text: 'Konton och belopp i de nuvarande konteringsraderna ersätts av mallen.',
                icon: 'warning',
                buttons: ['Behåll nuvarande', 'Ersätt med mall'],
            });
            if (!shouldReplace) return;
        }

        const timestamp = Date.now();
        const nextRows = rows.map((row, index) => ({ ...row, id: `${timestamp}-${index}` }));
        while (nextRows.length < 2) {
            nextRows.push({
                id: `${timestamp}-${nextRows.length}`,
                account_id: '',
                account_number: '',
                account_name: '',
                debit: '',
                credit: '',
            });
        }

        setManualRows(nextRows);
        setAppliedTemplate({
            id: template.template_id,
            name: template.template_name,
        });
        if (!description.trim()) setDescription(template.template_name || '');
        setValidationAttempted(false);
        setShowTemplatePicker(false);

        if (unresolvedCount > 0) {
            toast.info(`${unresolvedCount} konto från mallen behöver väljas manuellt.`);
        } else if (evaluation) {
            toast.success(`${rows.length} konteringsrader beräknades från mallen.`);
        } else {
            toast.success(`${rows.length} konteringsrader skapades från mallen.`);
        }
    };

    const handleSave = () => {
        setValidationAttempted(true);
        if (validationIssues.length > 0) {
            toast.error(validationIssues[0].message);
            if (issueKeys.has('description')) descriptionRef.current?.focus();
            return;
        }
        handleAddVerification();
    };

    useEffect(() => {
        const handleKeyboardSave = (event) => {
            if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
                event.preventDefault();
                if (!savingVerification && !uploadingFiles) handleSave();
            }
        };

        window.addEventListener('keydown', handleKeyboardSave);
        return () => window.removeEventListener('keydown', handleKeyboardSave);
    });

    const handleAbort = () => {
        const hasChanges = initialDraftRef.current !== createDraftSnapshot({
            date,
            description,
            reference,
            comments,
            attachments,
            rows: manualRows,
        });

        if (hasChanges) {
            swal({
                title: "Är du säker?",
                text: "Ändringar du gjort kommer att gå förlorade",
                icon: "warning",
                buttons: ["Avbryt", "Ja, avbryt"],
                dangerMode: true,
            }).then((willDiscard) => {
                if (willDiscard) {
                    navigateBack();
                }
            });
        } else {
            navigateBack();
        }
    };

    const navigateBack = () => {
        history.push(returnTo);
    };

    const handleAddVerification = async () => {
        setSavingVerification(true);
        setError(null);

        try {
            const theDate = formatLocalDate(date);

            // Format transactions
            const transactions = manualRows.map(row => {
                // Determine transaction type based on debit or credit
                const debit = amountToCents(row.debit);
                const credit = amountToCents(row.credit);
                const transaction_type = debit > 0 ? 'debit' : 'credit';
                const amount = (debit > 0 ? debit : credit) / 100;

                return {
                    account_id: row.account_id,
                    transaction_type,
                    amount
                };
            });

            // Format attachments for API
            const formattedAttachments = attachments.map(att => ({
                attachmentId: att.attachmentId,
                blobName: att.blobName,
                fileUrl: att.fileUrl,
                file_name: att.original_filename,
                file_type: att.file_type,
                file_size: att.file_size,
                blob_name: att.blobName,
                file_url: att.fileUrl
            }));

            const verificationData = {
                    date: theDate,
                    description: description.trim(),
                    reference: reference.trim() || null,
                    notes: comments.trim() || null,
                    series: editSource?.series || 'V',
                    transactions,
                    attachments: formattedAttachments
            };

            const response = isEditMode
                ? await axios.post(
                    getApiUrl(`${companyId}/verifications/${verificationId}/replace`),
                    {
                        annulment: {
                            fiscalYearId: editSource.fiscal_year_id,
                            date: String(editSource.date || '').slice(0, 10),
                            description: `Annullering av verifikat ${editSource.series}${editSource.number}`,
                            reference: `Annullering-${editSource.series}${editSource.number}`,
                            notes: 'Systemskapad rättelse',
                            series: editSource.series || 'V',
                            transactions: editSource.transactions.map((transaction) => ({
                                account_id: transaction.account_id,
                                amount: Number(transaction.amount),
                                transaction_type: transaction.transaction_type === 'debit' ? 'credit' : 'debit',
                                transaction_description: transaction.description || transaction.transaction_description,
                            })),
                        },
                        replacement: {
                            ...verificationData,
                            fiscalYearId: activeFiscalYear?.fiscal_year_id,
                        },
                    },
                    { withCredentials: true }
                )
                : await axios.post(
                getApiUrl(`${companyId}/verifications`),
                verificationData,
                { withCredentials: true }
            );

            if (response.data.success) {
                if (appliedTemplate?.id) recordTemplateUsage(companyId, appliedTemplate.id);
                toast.success(isEditMode
                    ? 'Rättelsen har sparats som ett nytt verifikat.'
                    : 'Verifikatet har skapats och lagts till i listan');
                const separator = returnTo.includes('?') ? '&' : '?';
                history.push(`${returnTo}${separator}updated=true`);
            } else {
                throw new Error(response.data.message || (isEditMode
                    ? 'Det gick inte att spara rättelsen.'
                    : 'Det gick inte att skapa verifikatet'));
            }
        } catch (error) {
            setError(error.response?.data?.message || (isEditMode
                ? 'Det gick inte att spara rättelsen. Försök igen senare.'
                : 'Det gick inte att skapa verifikatet. Försök igen senare.'));
        } finally {
            setSavingVerification(false);
        }
    };

    const sourceLabel = editSource
        ? `${editSource.series || ''}${editSource.number || ''}`
        : copySource
            ? `${copySource.series || ''}${copySource.number || ''}`
            : '';
    const pageTitle = isEditMode
        ? `Redigera verifikat${sourceLabel ? ` ${sourceLabel}` : ''}`
        : copySource
            ? 'Kopiera verifikat'
            : 'Skapa verifikat';
    const pageSubtitle = isEditMode
        ? 'Justera konteringen nedan. När du sparar skapas en annullering och ett nytt rättat verifikat.'
        : copySource
            ? `Använd ${sourceLabel || 'verifikatet'} som utgångspunkt och anpassa konteringen.`
            : 'Direkt kontering med debet och kredit.';
    const PageIcon = isEditMode ? FilePenLine : copySource ? Copy : FilePlus;

    if (loadingVerification || (loading && accounts.length === 0)) {
        return (
            <div className="container-fluid">
                <PageHeader
                  icon={PageIcon}
                  title={pageTitle}
                  subtitle={pageSubtitle}
                  backTo={{ to: returnTo, label: 'Till verifikationer' }}
                />
                <div className="text-center py-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Laddar...</span>
                    </Spinner>
                    <p className="mt-2">{isEditMode ? 'Laddar verifikat…' : 'Laddar kontoplan…'}</p>
                </div>
            </div>
        );
    }

    if (isEditMode && !editSource) {
        return (
            <div className="container-fluid manual-entry-page">
                <PageHeader
                  icon={PageIcon}
                  title="Redigera verifikat"
                  subtitle="Verifikatet kunde inte öppnas för redigering."
                  backTo={{ to: returnTo, label: 'Till verifikationer' }}
                />
                <Alert variant="danger">{error || 'Det gick inte att hämta verifikatet.'}</Alert>
            </div>
        );
    }

    return (
        <div className="container-fluid manual-entry-page">

            <PageHeader
              icon={PageIcon}
              title={pageTitle}
              subtitle={pageSubtitle}
              backTo={{
                  to: returnTo,
                  label: 'Till verifikationer',
                  onClick: (event) => {
                      event.preventDefault();
                      handleAbort();
                  },
              }}
            />

            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
            )}

            <Row className="manual-entry">
                <Col lg={12}>
                    <div className="manual-entry__surface">
                        {copySource && (
                            <div className="manual-entry__copy-context">
                                <span>Utgår från {sourceLabel}</span>
                                <small>Konton och belopp är förifyllda och kan ändras. Originalet påverkas inte.</small>
                            </div>
                        )}
                        {isEditMode && (
                            <div className="manual-entry__copy-context">
                                <span>Rättar {sourceLabel}</span>
                                <small>
                                    Två nya verifikat skapas: ett som tar ut originalet och ett med konteringen nedan.
                                    Originalet ligger kvar och låses.
                                </small>
                            </div>
                        )}
                        <section className="manual-entry__section" aria-labelledby="verification-details-heading">
                            <h2 id="verification-details-heading" className="manual-entry__section-title">Grunduppgifter</h2>
                            <Row className="g-3">
                                <Col lg={6}>
                                    <Form.Group controlId="verification-description">
                                        <Form.Label>Beskrivning <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            ref={descriptionRef}
                                            type="text"
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                            placeholder="Vad gäller verifikatet?"
                                            disabled={savingVerification}
                                            isInvalid={validationAttempted && issueKeys.has('description')}
                                        />
                                        <Form.Control.Feedback type="invalid">Ange en beskrivning.</Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col sm={6} lg={3}>
                                    <Form.Group controlId="verification-date">
                                        <Form.Label>Datum <span className="text-danger">*</span></Form.Label>
                                    <DatePicker
                                        id="verification-date"
                                        selected={date}
                                        onChange={setDate}
                                        dateFormat="yyyy-MM-dd"
                                        className={`form-control ${validationAttempted && issueKeys.has('fiscalYear') ? 'is-invalid' : ''}`}
                                        disabled={savingVerification}
                                    />
                                        <div className={`manual-entry__fiscal-status ${activeFiscalYear?.state === 'closed' || !activeFiscalYear ? 'is-locked' : ''}`}>
                                            {activeFiscalYear?.state === 'closed' || !activeFiscalYear
                                                ? <Lock size={13} aria-hidden="true" />
                                                : <LockOpen size={13} aria-hidden="true" />}
                                            {activeFiscalYear
                                                ? `Räkenskapsår ${activeFiscalYear.year} · ${activeFiscalYear.state === 'closed' ? 'Låst' : 'Öppet'}`
                                                : 'Datumet tillhör inget räkenskapsår'}
                                        </div>
                                    </Form.Group>
                                </Col>
                                <Col sm={6} lg={3}>
                                    <Form.Group controlId="verification-reference">
                                        <Form.Label>Referens <span className="manual-entry__optional">Valfritt</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={reference}
                                            onChange={(e) => setReference(e.target.value)}
                                            placeholder="Till exempel fakturanummer"
                                            disabled={savingVerification}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </section>

                        <section className="manual-entry__section" aria-labelledby="verification-postings-heading">
                            <div className="manual-entry__posting-heading">
                                <div>
                                    <h2 id="verification-postings-heading" className="manual-entry__section-title">Konteringsrader</h2>
                                    <p className="manual-entry__section-hint">Sök på kontonummer eller kontonamn.</p>
                                    <AccountPickerScope
                                        showAll={showAllAccounts}
                                        onToggle={() => setShowAllAccounts((v) => !v)}
                                        defaultCount={countDefaultPickerAccounts(accounts)}
                                        totalCount={accounts.length}
                                        hasCompanyAccounts={hasCompanyAccounts(accounts)}
                                        disabled={savingVerification}
                                        style={{ marginTop: 0 }}
                                    />
                                </div>
                                <div className={`manual-entry__balance ${!hasPostingAmounts ? 'is-empty' : isBalanced ? 'is-balanced' : 'is-unbalanced'}`} role="status">
                                    {!hasPostingAmounts ? (
                                        <>Inte påbörjat</>
                                    ) : isBalanced ? (
                                        <><CheckCircle size={15} /> Balanserar</>
                                    ) : (
                                        <><AlertTriangle size={15} /> Differens {formatCurrency(differenceCents / 100)}</>
                                    )}
                                </div>
                            </div>

                        <div className="manual-entry__posting-table">
                            <div className="table-responsive">
                                <table className="table mb-0 manual-entry__table">
                                    <thead>
                                        <tr>
                                            <th>Konto</th>
                                            <th>Kontonamn</th>
                                            <th className="text-end">Debet</th>
                                            <th className="text-end">Kredit</th>
                                            <th><span className="visually-hidden">Radåtgärder</span></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {manualRows.map((row, index) => (
                                            <tr
                                                key={row.id}
                                                className={validationAttempted && validationIssues.some((issue) => issue.row === index) ? 'has-error' : ''}
                                            >
                                                <td data-label="Konto">
                                                    <AccountSelect
                                                        inputId={`verification-account-${index}`}
                                                        aria-label={`Konto på rad ${index + 1}`}
                                                        options={pickerOptions}
                                                        allOptions={accounts}
                                                        value={accounts.find((option) => option.value === row.account_id) || null}
                                                        onChange={(selectedOption) =>
                                                            handleRowChange(index, "account_id", selectedOption ? selectedOption.value : "")
                                                        }
                                                        valueDisplay="number"
                                                        isDisabled={savingVerification}
                                                        placeholder={row.suggested_account_number
                                                            ? `Konto ${row.suggested_account_number} saknas – välj konto…`
                                                            : undefined}
                                                        styles={{
                                                            control: (base, state) => ({
                                                                ...base,
                                                                minWidth: "150px",
                                                                minHeight: '38px',
                                                                borderRadius: '8px',
                                                                borderColor: state.isFocused ? '#44814E' : '#aeb6c2',
                                                                boxShadow: state.isFocused ? '0 0 0 3px rgba(68, 129, 78, 0.20)' : 'none',
                                                                '&:hover': { borderColor: state.isFocused ? '#44814E' : '#8793a1' },
                                                            }),
                                                        }}
                                                    />
                                                </td>
                                                <td data-label="Kontonamn">
                                                    <Form.Control
                                                        type="text"
                                                        className={`manual-entry__account-name ${row.account_name ? '' : 'is-empty'}`}
                                                        value={row.account_name}
                                                        placeholder="Kontonamn"
                                                        readOnly
                                                        aria-label={`Kontonamn på rad ${index + 1}`}
                                                        title={row.account_name || undefined}
                                                    />
                                                </td>
                                                <td data-label="Debet">
                                                    <Form.Control
                                                        type="text"
                                                        inputMode="decimal"
                                                        aria-label={`Debet på rad ${index + 1}`}
                                                        value={row.debit}
                                                        onChange={(e) => handleRowChange(index, "debit", e.target.value)}
                                                        placeholder={row.template_side === 'credit' ? '—' : row.suggested_side === 'debit' ? 'Belopp' : '0,00'}
                                                        disabled={savingVerification || row.template_side === 'credit' || amountToCents(row.credit) > 0}
                                                        title={row.template_side === 'credit' ? 'Mallen placerar beloppet i kredit.' : undefined}
                                                        className="manual-entry__amount text-end"
                                                        isInvalid={validationAttempted && issueKeys.has(`row-${index}-amount`)}
                                                    />
                                                </td>
                                                <td data-label="Kredit">
                                                    <Form.Control
                                                        type="text"
                                                        inputMode="decimal"
                                                        aria-label={`Kredit på rad ${index + 1}`}
                                                        value={row.credit}
                                                        onChange={(e) => handleRowChange(index, "credit", e.target.value)}
                                                        placeholder={row.template_side === 'debit' ? '—' : row.suggested_side === 'credit' ? 'Belopp' : '0,00'}
                                                        disabled={savingVerification || row.template_side === 'debit' || amountToCents(row.debit) > 0}
                                                        title={row.template_side === 'debit' ? 'Mallen placerar beloppet i debet.' : undefined}
                                                        className="manual-entry__amount text-end"
                                                        isInvalid={validationAttempted && issueKeys.has(`row-${index}-amount`)}
                                                    />
                                                </td>
                                                <td className="text-center">
                                                    {manualRows.length > 2 && (
                                                        <button
                                                            type="button"
                                                            className="manual-entry__remove-row"
                                                            onClick={() => handleRemoveRow(index)}
                                                            disabled={savingVerification}
                                                            aria-label={`Ta bort konteringsrad ${index + 1}`}
                                                        >
                                                            <Trash2 size={15} />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan="2">Summa</td>
                                            <td className="text-end manual-entry__total">
                                                {formatCurrency(totalDebit)}
                                            </td>
                                            <td className="text-end manual-entry__total">
                                                {formatCurrency(totalCredit)}
                                            </td>
                                            <td></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>

                        <div className="manual-entry__posting-actions">
                            <button
                                type="button"
                                className="manual-entry__text-action"
                                onClick={handleAddRow}
                                disabled={savingVerification}
                            >
                                <Plus size={15} /> Lägg till rad
                            </button>
                            <button
                                type="button"
                                className="manual-entry__text-action"
                                onClick={() => setShowTemplatePicker(true)}
                                disabled={savingVerification}
                            >
                                <LayoutTemplate size={15} /> Från mall
                            </button>
                            {appliedTemplate && (
                                <span className="manual-entry__applied-template" title={appliedTemplate.name}>
                                    Mall: {appliedTemplate.name}
                                </span>
                            )}
                            {differenceCents > 0 && (
                                <button
                                    type="button"
                                    className="manual-entry__text-action"
                                    onClick={handleFillDifference}
                                    disabled={savingVerification}
                                >
                                    <WandSparkles size={15} /> Fyll differens på tom rad
                                </button>
                            )}
                        </div>
                        {validationAttempted && validationIssues.some((issue) => issue.row !== undefined) && (
                            <div className="manual-entry__row-error" role="alert">
                                {validationIssues.find((issue) => issue.row !== undefined)?.message}
                            </div>
                        )}
                        </section>

                        <div className="manual-entry__optional-actions" aria-label="Valfria uppgifter">
                            <button
                                type="button"
                                className="manual-entry__text-action"
                                aria-expanded={showAttachmentsSection}
                                aria-controls="verification-attachments-section"
                                onClick={() => setShowAttachmentsSection((visible) => !visible)}
                                disabled={savingVerification || uploadingFiles}
                            >
                                <Paperclip size={15} /> {showAttachmentsSection
                                    ? 'Dölj underlag'
                                    : originalAttachments.length > 0
                                        ? 'Visa underlag'
                                        : 'Lägg till underlag'}
                            </button>
                            <button
                                type="button"
                                className="manual-entry__text-action"
                                aria-expanded={showCommentsSection}
                                aria-controls="verification-comment-section"
                                onClick={() => setShowCommentsSection((visible) => !visible)}
                                disabled={savingVerification}
                            >
                                <MessageSquare size={15} /> {showCommentsSection ? 'Dölj kommentar' : 'Lägg till kommentar'}
                            </button>
                        </div>

                        {showAttachmentsSection && (
                            <section
                                id="verification-attachments-section"
                                className="manual-entry__section manual-entry__optional-section"
                                aria-labelledby="verification-attachments-heading"
                            >
                                <div className="manual-entry__section-heading">
                                    <div>
                                        <h2 id="verification-attachments-heading" className="manual-entry__section-title">Underlag</h2>
                                        <p className="manual-entry__section-hint">
                                            {isEditMode
                                                ? 'Befintliga underlag ligger kvar på originalet. Nya underlag kopplas till rättelsen.'
                                                : 'Lägg till kvitto, faktura eller annat underlag.'}
                                        </p>
                                    </div>
                                    <span className="manual-entry__optional">Valfritt</span>
                                </div>
                                <div className="manual-entry__attachments">
                                    {[...originalAttachments, ...attachments].map((attachment) => (
                                        <div
                                            key={attachment.id}
                                            className={`manual-entry__attachment${attachment.isOriginalAttachment ? ' is-original' : ''}`}
                                        >
                                            <button
                                                type="button"
                                                className="manual-entry__attachment-main"
                                                onClick={() => openAttachmentPreview(attachment)}
                                                disabled={!canPreviewFile(attachment.original_filename)}
                                                title={canPreviewFile(attachment.original_filename) ? 'Förhandsgranska underlag' : attachment.original_filename}
                                            >
                                                <span className="manual-entry__file-icon"><File size={17} /></span>
                                                <span className="manual-entry__file-copy">
                                                    <span className="manual-entry__file-name">{attachment.original_filename}</span>
                                                    <span className="manual-entry__file-size">
                                                        {formatFileSize(attachment.file_size)}
                                                        {attachment.isOriginalAttachment && ' · På originalet'}
                                                    </span>
                                                </span>
                                            </button>
                                            {!attachment.isOriginalAttachment && (
                                                <button
                                                    type="button"
                                                    className="manual-entry__attachment-remove"
                                                    onClick={() => removeAttachment(attachment.attachmentId)}
                                                    disabled={savingVerification}
                                                    aria-label={`Ta bort ${attachment.original_filename}`}
                                                >
                                                    <X size={14} />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                    <input
                                        type="file"
                                        id="manual-entry-file-upload"
                                        multiple
                                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.txt"
                                        onChange={handleFileUpload}
                                        className="visually-hidden"
                                        disabled={uploadingFiles || savingVerification}
                                    />
                                    <label htmlFor="manual-entry-file-upload" className="manual-entry__upload">
                                        {uploadingFiles ? <Spinner animation="border" size="sm" /> : <Paperclip size={16} />}
                                        <span>{uploadingFiles ? 'Laddar upp…' : 'Lägg till underlag'}</span>
                                        <small>PDF, bild, Word eller text · max 10 MB</small>
                                    </label>
                                </div>
                            </section>
                        )}

                        {showCommentsSection && (
                            <section
                                id="verification-comment-section"
                                className="manual-entry__section manual-entry__optional-section"
                                aria-labelledby="verification-comment-heading"
                            >
                                <div className="manual-entry__section-heading">
                                    <div>
                                        <h2 id="verification-comment-heading" className="manual-entry__section-title">Kommentar</h2>
                                        <p className="manual-entry__section-hint">Intern anteckning som sparas med verifikatet.</p>
                                    </div>
                                    <span className="manual-entry__optional">Valfritt</span>
                                </div>
                                <Form.Group controlId="verification-comments">
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={comments}
                                        onChange={(e) => setComments(e.target.value)}
                                        placeholder="Lägg till en intern kommentar…"
                                        disabled={savingVerification}
                                        aria-label="Kommentar"
                                    />
                                </Form.Group>
                            </section>
                        )}

                        <div className="manual-entry__actions">
                            <button type="button" className="manual-entry__cancel" onClick={handleAbort} disabled={savingVerification}>
                                Avbryt
                            </button>
                            <div className="manual-entry__save-group">
                                <span className="manual-entry__shortcut">Ctrl/⌘ + Enter</span>
                                <button
                                    type="button"
                                    className="btn btn-primary manual-entry__save"
                                    onClick={handleSave}
                                    disabled={savingVerification || uploadingFiles || isFiscalYearLocked}
                                    title={isFiscalYearLocked ? 'Välj ett datum i ett öppet räkenskapsår.' : undefined}
                                >
                                    {savingVerification ? (
                                        <><FontAwesomeIcon icon={faSpinner} spin /> {isEditMode ? 'Skapar annullering och rättelse…' : 'Skapar verifikat…'}</>
                                    ) : (
                                        <><Save size={16} /> {isEditMode ? 'Skapa annullering och rättelse' : 'Skapa verifikat'}</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </Col>

            </Row>

            <Modal show={Boolean(previewAttachment)} onHide={closeAttachmentPreview} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title className="manual-entry__preview-title">
                        {previewAttachment?.original_filename}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="manual-entry__preview-body">
                    {previewLoading ? (
                        <div className="manual-entry__preview-loading">
                            <Spinner animation="border" size="sm" />
                            <span>Laddar underlag…</span>
                        </div>
                    ) : previewText !== null ? (
                        <pre className="manual-entry__preview-text">{previewText}</pre>
                    ) : previewUrl && /\.pdf$/i.test(previewAttachment?.original_filename || '') ? (
                        <iframe src={previewUrl} title={previewAttachment?.original_filename} />
                    ) : previewUrl ? (
                        <img src={previewUrl} alt={previewAttachment?.original_filename} />
                    ) : null}
                </Modal.Body>
            </Modal>
            <ManualTemplatePickerModal
                show={showTemplatePicker}
                onHide={() => setShowTemplatePicker(false)}
                onApply={handleApplyTemplate}
                companyId={companyId}
                accounts={accounts}
            />
        </div>
    );
};

export default CreateVerification;
